import {
  alertsResponseSchema,
  chargesTodayResponseSchema,
  divergenceAlertResolveBulkResponseSchema,
  divergenceAlertResolveResponseSchema,
  divergenceAlertTypesResponseSchema,
  divergenceAlertsResponseSchema,
  ignoreNumberResponseSchema,
  ignoredNumbersResponseSchema,
  inadimplentesResponseSchema,
  kpisResponseSchema,
  linkClientResponseSchema,
  removeIgnoredNumberResponseSchema,
  resendReminderResponseSchema,
  revenueByProductResponseSchema,
  revenueMonthlyResponseSchema,
  type AlertsResponse,
  type ChargesTodayResponse,
  type DashboardPeriod,
  type DivergenceAlertResolveBulkResponse,
  type DivergenceAlertResolveResponse,
  type DivergenceAlertTypesResponse,
  type DivergenceAlertsResponse,
  type IgnoreNumberResponse,
  type IgnoredNumbersResponse,
  type InadimplentesResponse,
  type KpisResponse,
  type LinkClientResponse,
  type LinkClientTarget,
  type RemoveIgnoredNumberResponse,
  type ResendReminderResponse,
  type RevenueByProductResponse,
  type RevenueMonthlyResponse,
} from '@control-center/shared';

import { apiClient } from './api-client';

export async function fetchDashboardKpis(
  period: DashboardPeriod = 'current_month',
): Promise<KpisResponse> {
  const res = await apiClient.get<unknown>(`/api/dashboard/kpis?period=${period}`);
  return kpisResponseSchema.parse(res.data);
}

export async function fetchDashboardAlerts(): Promise<AlertsResponse> {
  const res = await apiClient.get<unknown>('/api/dashboard/alerts');
  return alertsResponseSchema.parse(res.data);
}

export async function fetchInadimplentes(limit = 10): Promise<InadimplentesResponse> {
  const res = await apiClient.get<unknown>(`/api/dashboard/inadimplentes?limit=${limit}`);
  return inadimplentesResponseSchema.parse(res.data);
}

export async function resendChargeReminder(chargeId: string): Promise<ResendReminderResponse> {
  const res = await apiClient.post<unknown>(
    `/api/dashboard/inadimplentes/${chargeId}/resend-reminder`,
    {},
  );
  return resendReminderResponseSchema.parse(res.data);
}

export async function fetchChargesToday(limit = 50): Promise<ChargesTodayResponse> {
  const res = await apiClient.get<unknown>(`/api/dashboard/charges-today?limit=${limit}`);
  return chargesTodayResponseSchema.parse(res.data);
}

export async function fetchRevenueByProduct(limit = 10): Promise<RevenueByProductResponse> {
  const res = await apiClient.get<unknown>(`/api/dashboard/revenue-by-product?limit=${limit}`);
  return revenueByProductResponseSchema.parse(res.data);
}

export async function fetchRevenueMonthly(months = 12): Promise<RevenueMonthlyResponse> {
  const res = await apiClient.get<unknown>(`/api/dashboard/revenue-monthly?months=${months}`);
  return revenueMonthlyResponseSchema.parse(res.data);
}

export interface DivergenceAlertsParams {
  resolved?: boolean;
  type?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchDivergenceAlerts(
  params: DivergenceAlertsParams = {},
): Promise<DivergenceAlertsResponse> {
  const qs = new URLSearchParams();
  if (params.resolved !== undefined) qs.set('resolved', String(params.resolved));
  if (params.type) qs.set('type', params.type);
  if (params.q) qs.set('q', params.q);
  qs.set('page', String(params.page ?? 1));
  qs.set('pageSize', String(params.pageSize ?? 20));
  const res = await apiClient.get<unknown>(`/api/divergence-alerts?${qs.toString()}`);
  return divergenceAlertsResponseSchema.parse(res.data);
}

export async function fetchDivergenceAlertTypes(): Promise<DivergenceAlertTypesResponse> {
  const res = await apiClient.get<unknown>('/api/divergence-alerts/types');
  return divergenceAlertTypesResponseSchema.parse(res.data);
}

export async function resolveDivergenceAlert(
  id: string,
  notes?: string,
): Promise<DivergenceAlertResolveResponse> {
  const body = notes ? { notes } : {};
  const res = await apiClient.post<unknown>(`/api/divergence-alerts/${id}/resolve`, body);
  return divergenceAlertResolveResponseSchema.parse(res.data);
}

export interface AsaasConnectivityResult {
  ok: boolean;
  httpStatus: number | null;
  accountName: string | null;
  environment: string | null;
  balance: number | null;
  responseTimeMs: number;
  errorMessage: string | null;
}

export async function testAsaasConnectivity(): Promise<AsaasConnectivityResult> {
  const res = await apiClient.post<{ data: AsaasConnectivityResult }>(
    '/api/diagnostics/asaas-connectivity',
  );
  return res.data.data;
}

export async function resolveDivergenceAlertsBulk(input: {
  type?: string;
  q?: string;
  notes?: string;
}): Promise<DivergenceAlertResolveBulkResponse> {
  const body: Record<string, string> = {};
  if (input.type) body.type = input.type;
  if (input.q) body.q = input.q;
  if (input.notes) body.notes = input.notes;
  const res = await apiClient.post<unknown>('/api/divergence-alerts/resolve-bulk', body);
  return divergenceAlertResolveBulkResponseSchema.parse(res.data);
}

// Story 5.11 — triagem de WhatsApp desconhecido

export async function ignoreAlertNumber(alertId: string): Promise<IgnoreNumberResponse> {
  const res = await apiClient.post<unknown>(`/api/divergence-alerts/${alertId}/ignore-number`, {});
  return ignoreNumberResponseSchema.parse(res.data);
}

export async function fetchIgnoredNumbers(): Promise<IgnoredNumbersResponse> {
  const res = await apiClient.get<unknown>('/api/divergence-alerts/ignored-numbers');
  return ignoredNumbersResponseSchema.parse(res.data);
}

export async function removeIgnoredNumber(id: string): Promise<RemoveIgnoredNumberResponse> {
  const res = await apiClient.delete<unknown>(`/api/divergence-alerts/ignored-numbers/${id}`);
  return removeIgnoredNumberResponseSchema.parse(res.data);
}

export interface LinkClientInput {
  clientId: string;
  target: LinkClientTarget;
  contactName?: string;
}

export async function linkAlertToClient(
  alertId: string,
  input: LinkClientInput,
): Promise<LinkClientResponse> {
  const body: Record<string, string> = { clientId: input.clientId, target: input.target };
  if (input.contactName) body.contactName = input.contactName;
  const res = await apiClient.post<unknown>(`/api/divergence-alerts/${alertId}/link-client`, body);
  return linkClientResponseSchema.parse(res.data);
}
